<template>
  <div class="text-black tab-content">
    <!-- ************************ -->
    <!-- card type settings begin -->
    <!-- ************************ -->
    <v-col class="flex-wrap" cols="12">
      <div class="mt-4 mb-3" style="font-size: 32px">
        Card Type Settings
      </div>
    </v-col>

    <v-col class="flex-wrap" cols="12">
      <div>
        <v-row class="flex-wrap align-center pl-1 mb-0 pb-2" style="border-bottom: 1px solid #E5E3E1">
          <v-col cols="1">
          </v-col>
          <v-col cols="2" class="text-left">
            Type name
          </v-col>
          <v-col cols="2" class="text-center">
            Show in backlog
          </v-col>
          <v-col cols="1" class="text-center text-no-wrap">
            Use AI
            <help-dialog-workflow-cards :settings="true" />
          </v-col>
          <v-col cols="2" class="text-center text-no-wrap">
            Command
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-icon size="21" color="#0000009e" v-bind="props">mds:info</v-icon>
              </template>
              <span>Pick one card type to be used for Commands in the 'Domain Model' section.</span>
            </v-tooltip>
          </v-col>
          <v-col cols="1" class="text-center text-no-wrap pl-0">
            Aggregate Root
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-icon size="21" color="#0000009e" v-bind="props">mds:info</v-icon>
              </template>
              <span>Pick one card type to be used for Aggregate Root in the 'Domain Model'.</span>
            </v-tooltip>
          </v-col>
          <v-col cols="2" class="text-center text-no-wrap">
            Read Model
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-icon size="21" color="#0000009e" v-bind="props">mds:info</v-icon>
              </template>
              <span>Pick one card type to be used for Read Models in the 'Domain Model' section.</span>
            </v-tooltip>
          </v-col>
          <v-col class="text-left">
          </v-col>
        </v-row>

        <div id="card-types" ref="cardTypes">
          <workflow-settings-requirement-type
            class="sortableRow"
            v-for="requirementType in requirementTypes"
            :key="requirementType.id"
            :data-id="requirementType.id"
            :requirement-type="requirementType"
            :disabled="$store.state.isWorkflowDisabled"
          />
        </div>

        <div class="d-flex align-center justify-space-between">
          <div id="default-type" class="d-flex flex-column gap-y-1 mt-6">
            <span>
              Default Type
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-icon size="20.5" v-bind="props">mdi-information-outline</v-icon>
                </template>
                <span>Default card type for new cards. Only enabled for types with SHOW IN BACKLOG selected.</span>
              </v-tooltip>
            </span>
            <v-select
              density="compact"
              variant="outlined"
              :items="requirementTypes.filter(rt => rt.showInBacklog)"
              v-model="defaultRequirementType"
              @update:model-value="handleDefaultTypeChange"
              return-object
              :disabled="$store.state.isWorkflowDisabled"
            >
              <template #item="{ item, props }">
                <div v-bind="props" class="d-flex align-center p-3 cursor-pointer">
                  <workflow-settings-requirement-type-color-picker
                    :color="item.raw.color && item.raw.color.rgb"
                    variant="cards"
                    :disabled="true"
                  />
                  <span class="ml-2">{{ item.raw.title }}</span>
                </div>
              </template>
              <template #selection="{ item }">
                <workflow-settings-requirement-type-color-picker
                  :color="item.raw.color && item.raw.color.rgb"
                  variant="cards"
                  :disabled="true"
                ></workflow-settings-requirement-type-color-picker>
                <span class="ml-2">{{ item.raw.title }}</span>
              </template>
            </v-select>
          </div>
          <div class="d-flex justify-end mt-2 mr-3">
            <v-btn rounded="lg" size="large" variant="flat" color="#363a40" @click="createRequirementType()"
                   :disabled="$store.state.isWorkflowDisabled">Add Type
            </v-btn>
          </div>
        </div>
      </div>
    </v-col>
    <!-- ********************** -->
    <!-- card type settings end -->
    <!-- ********************** -->
  </div>
</template>

<script>
import Sortable from 'sortablejs'
import midString from '@/utils/mid-string'
import WorkflowSettingsRequirementType from '@/components/WorkflowSettingsRequirementType'
import WorkflowSettingsRequirementTypeColorPicker from '@/components/WorkflowSettingsRequirementTypeColorPicker'
import store from '@/store'
import workflowApi from '@/api/workflowApi'
import requirementTypesApi from '@/api/requirementTypesApi'
// import throttle from '@/utils/throttle'
import debounce from '@/utils/debounce'
import { mapState } from 'vuex'
import HelpDialogWorkflowCards from '@/components/HelpDialogWorkflowCards'

export default {
  components: {
    WorkflowSettingsRequirementType,
    WorkflowSettingsRequirementTypeColorPicker,
    HelpDialogWorkflowCards
  },

  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow,
      requirementTypes: state => state.requirementTypes.requirementTypes
    })
  },

  data () {
    return {
      aiVersion: 0,
      createTypeOpen: false,
      createTypeFetching: false,
      validForm: true,
      isLoading: true,
      rules: {
        required: value => !!value || 'Required.'
      },
      saveAiDescriptionDebouncer: debounce(e => {
        this.saveAiDescription(e)
      }, 1000),
      aiModels: [
        { name: 'Qlerify', version: 5 },
        { name: 'OpenAI ChatGPT-4o', version: 6 },
        { name: 'OpenAI ChatGPT-4o-mini', version: 3 },
        { name: 'OpenAI ChatGPT-4', version: 4 }
      ],
      defaultRequirementType: undefined
    }
  },

  methods: {
    debouncedSaveAiDescription ({ description, skipDebounce }) {
      if (skipDebounce) {
        this.saveAiDescription(description)
      } else {
        this.saveAiDescriptionDebouncer(description)
      }
    },
    saveAiDescription (e) {
      workflowApi.updateWorkflowNoHistoryMutation(
        this.$route.params.projectId,
        {
          id: this.$route.params.id,
          expectedVersion: this.workflow.version,
          aiDescription: e
        }
      )
    },
    async createRequirementType () {
      let counter = 1
      while (this.requirementTypes.findIndex(rt => rt.title === `Card type ${counter}`) >= 0) {
        counter++
      }
      const title = `Card type ${counter}`
      const description = `Card type ${counter} description`
      const color = {
        red: 158,
        green: 158,
        blue: 158
      }
      const workflowId = this.$route.params.id
      this.createTypeFetching = true
      const input = {
        title,
        description,
        color,
        showInBacklog: true,
        triggerType: false,
        queryType: false,
        aggregateType: false,
        defaultType: false,
        order: midString(this.requirementTypes[this.requirementTypes.length - 1]?.order || '', '')
      }
      await store.dispatch('requirementTypes/createRequirementType', { workflowId, input })
      this.createTypeOpen = false
      this.createTypeFetching = false
    },
    initSortable () {
      this.sortable = new Sortable(this.$refs.cardTypes, {
        animation: 150,
        draggable: '.sortableRow',
        handle: '.sortHandle',
        ghostClass: 'sortable-ghost', // Class name for the drop placeholder
        chosenClass: 'sortable-chosen', // Class name for the chosen item
        dragClass: 'sortable-drag',
        scroll: true,
        fallbackOnBody: true,
        forceFallback: true,
        scrollSensitivity: 100,
        dataIdAttr: 'data-id',
        onStart: function (event) {
          Array.prototype.forEach.call(
            document.querySelectorAll('.v-row-group__header'),
            function (el, index) {
              if (!el.classList.contains('sortableRow') && index !== 0) {
                el.classList.add('sortableRow')
              }
            }
          )
          Array.prototype.forEach.call(
            document.querySelectorAll('.sortableRow:not(.sortable-chosen)'),
            function (el, index) {
              if (!el.classList.contains('white-background-on-hover') && index !== 0) {
                el.classList.add('white-background-on-hover')
              }
            }
          )
        },
        onEnd: this.handleEndDrag
      })
    },
    async handleEndDrag (event) {
      let midOrder = ''
      const count = this.requirementTypes.length

      // Validate `requirementTypes` array
      if (!Array.isArray(this.requirementTypes) || count === 0) {
        console.error('Invalid requirementTypes array.')
        return
      }

      // Check if orders need initialization
      const isInit = this.requirementTypes.some(rt => rt.order === null)
      let newOrders = Array(count).fill('')

      if (isInit) {
        // Initialize orders
        newOrders[0] = midString('', '')
        for (let i = 1; i < count; ++i) {
          newOrders[i] = midString(newOrders[i - 1], '')
        }
      } else {
        newOrders = this.requirementTypes.map(rt => rt.order)
      }

      // Determine movement direction
      const isMovingDown = event.newIndex > event.oldIndex

      // Calculate new order
      if (isMovingDown) {
        const nextOrder = event.newIndex + 1 < count ? newOrders[event.newIndex + 1] : ''
        midOrder = midString(newOrders[event.newIndex], nextOrder)
      } else {
        const prevOrder = event.newIndex > 0 ? newOrders[event.newIndex - 1] : ''
        midOrder = midString(prevOrder, newOrders[event.newIndex])
      }

      // Update the moved item's order
      newOrders[event.oldIndex] = midOrder

      // Update requirement types via API
      if (isInit) {
        // Update all items if orders were initialized
        for (let i = 0; i < count; ++i) {
          await requirementTypesApi.updateRequirementType(this.$route.params.id, this.requirementTypes[i], {
            order: newOrders[i]
          })
        }
      } else {
        // Update only the moved item
        await requirementTypesApi.updateRequirementType(this.$route.params.id, this.requirementTypes[event.oldIndex], {
          order: midOrder
        })
      }
    },
    async handleDefaultTypeChange (newDefaultType) {
      if (this.defaultRequirementType.id === newDefaultType.id) {
        return
      }

      if (this.defaultRequirementType) {
        await requirementTypesApi.updateRequirementType(this.$route.params.id, this.defaultRequirementType, { defaultType: false })
      }

      await requirementTypesApi.updateRequirementType(this.$route.params.id, newDefaultType, { defaultType: true })
      this.defaultRequirementType = newDefaultType
    }
  },

  mounted () {
    this.initSortable()

    this.defaultRequirementType = this.requirementTypes.find(rt => rt.defaultType)
  }
}
</script>

<style scoped>
.tab-content {
  height: 900px;
}

:deep(.v-select__menu-icon) {
  display: none !important;
}

.sortableRow > div:focus-within {
  outline: 2px solid #4d90fe;
}

.sortable-chosen {
  background: #eee;
}

.sortable-ghost {
  background: #E5E3E1;
}

.sortable-ghost > div {
  opacity: 0.01;
}

.p-3 {
  padding: 12px;
}

.gap-y-1 {
  row-gap: 8px;
}

#default-type {
  span {
    font-weight: 600;
  }
}
</style>
